import type { OverrideSessionPeriodPayload } from '../document/override-session';
import type { ProductType } from '../enums/product';
import { GetDetailsResponse, GetListResponse } from './common';

export enum OverrideSessionKeys {
  id = 'id',
  name = 'name',
  description = 'description',
  subtitle = 'subtitle',
  period = 'period',
  state = 'state',
  createdByName = 'createdByName',
  createdByEmail = 'createdByEmail',
  createdAt = 'createdAt',
  updatedAt = 'updatedAt',
  finishedAt = 'finishedAt',
  hasMasterPaidData = 'hasMasterPaidData',
  reportsCount = 'reportsCount',
  confirmedReportsCount = 'confirmedReportsCount',
  totalNetDueOverride = 'totalNetDueOverride',
  uncalculatedReportsCount = 'uncalculatedReportsCount',
}

export enum OverrideSessionStateType {
  draft = 'draft',
  open = 'open',
  finished = 'finished',
}

export type OverrideSessionState = 'draft' | 'open' | 'finished';

export enum OverrideSessionMasterPaidMutationKeys {
  sessionId = 'sessionId',
  deletedCount = 'deletedCount',
  insertedCount = 'insertedCount',
  range = 'range',
}

export interface OverrideSessionResponse {
  [OverrideSessionKeys.id]: number;
  [OverrideSessionKeys.name]: string;
  [OverrideSessionKeys.description]: string | null;
  [OverrideSessionKeys.subtitle]: string | null;
  [OverrideSessionKeys.period]: OverrideSessionPeriodPayload | null;
  [OverrideSessionKeys.state]: OverrideSessionState;
  [OverrideSessionKeys.createdByName]: string | null;
  [OverrideSessionKeys.createdByEmail]: string | null;
  [OverrideSessionKeys.createdAt]: string;
  [OverrideSessionKeys.updatedAt]: string;
  [OverrideSessionKeys.finishedAt]: string | null;
}

export interface OverrideSessionListItemResponse extends OverrideSessionResponse {
  [OverrideSessionKeys.reportsCount]: number;
  [OverrideSessionKeys.confirmedReportsCount]: number;
  [OverrideSessionKeys.totalNetDueOverride]: number;
  [OverrideSessionKeys.uncalculatedReportsCount]: number;
}

export interface OverrideSessionActiveResponseData extends OverrideSessionListItemResponse {
  [OverrideSessionKeys.hasMasterPaidData]: boolean;
}

export interface OverrideSessionGetListResponse extends GetListResponse<OverrideSessionListItemResponse> {
  data: OverrideSessionListItemResponse[];
  totalCount: number;
}

export interface OverrideSessionMasterPaidMutationResponse {
  [OverrideSessionMasterPaidMutationKeys.sessionId]: number;
  [OverrideSessionMasterPaidMutationKeys.deletedCount]: number;
  [OverrideSessionMasterPaidMutationKeys.insertedCount]: number;
  [OverrideSessionMasterPaidMutationKeys.range]: {
    startDate: string;
    endDate: string;
  } | null;
}

export type OverrideSessionDetailsResponse = GetDetailsResponse<OverrideSessionResponse>;

export type OverrideSessionActiveResponse = GetDetailsResponse<OverrideSessionActiveResponseData | null>;

export type OverrideSessionMasterPaidDetailsResponse = GetDetailsResponse<OverrideSessionMasterPaidMutationResponse>;

export interface OverrideSessionMasterPaidMissingProductResponse {
  id: number | null;
  product_type: ProductType | null;
  is_ny_product: boolean | null;
  product_name: string;
  carrier: string | null;
  is_active: boolean | null;
  required_user_input: boolean | null;
  comment: string | null;
}

export type OverrideSessionMasterPaidMissingProductsResponse = GetDetailsResponse<
  OverrideSessionMasterPaidMissingProductResponse[]
>;
